import type { SubjectLeftAction } from '@Application/Subjects/Actions/subjectLeft';
import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { AboutCandidatesFetchedAction } from '@Application/Subjects/Actions/aboutCandidatesFetched';
import type { SubjectArchivedAction } from '@Application/Subjects/Archivals/Actions/subjectArchived';
import type { SubjectRestoredAction } from '@Application/Subjects/Archivals/Actions/subjectRestored';
import type { SubjectCreatedAction } from '@Application/Subjects/Actions/subjectCreated';
import type { SubjectFetchedAction } from '@Application/Subjects/Actions/subjectFetched';
import type { SubjectDirectoryFetchedAction } from '@Application/Subjects/SubjectDirectory/Actions/subjectDirectoryFetched';
import type { SubjectUpdatedAction } from '@Application/Subjects/Actions/subjectUpdated';
import type { SubjectsFetchedAction } from '@Application/Subjects/Actions/subjectsFetched';
import type { SubjectMembersFetchedAction } from '@Application/Subjects/Members/Actions/subjectMembersFetched';
import type { SubjectUsersFetchedAction } from '@Application/Subjects/Members/Actions/subjectUsersFetched';
import AboutCandidateView from '@UI/Store/Subjects/Views/AboutCandidateView';
import AboutCandidatesListView from '@UI/Store/Subjects/Views/AboutCandidatesListView';
import SubjectDirectoryEntryView from '@UI/Store/Subjects/Views/SubjectDirectoryEntryView';
import SubjectDirectoryListView from '@UI/Store/Subjects/Views/SubjectDirectoryListView';
import SubjectMemberView from '@UI/Store/Subjects/Views/SubjectMemberView';
import SubjectMembersListView from '@UI/Store/Subjects/Views/SubjectMembersListView';
import SubjectUserView from '@UI/Store/Subjects/Views/SubjectUserView';
import SubjectUsersListView from '@UI/Store/Subjects/Views/SubjectUsersListView';
import SubjectView from '@UI/Store/Subjects/Views/SubjectView';
import SubjectsListView from '@UI/Store/Subjects/Views/SubjectsListView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type SubjectsStore = {
	subject: SubjectView | null;
	subjects: SubjectsListView | null;
	aboutCandidates: AboutCandidatesListView | null;
	members: SubjectMembersListView | null;
	users: SubjectUsersListView | null;
	// Kept apart from `subjects`: the unfiltered facility-wide directory must not overwrite a screen's page.
	directory: SubjectDirectoryListView | null;
};

const initialState: SubjectsStore = {
	subject: null,
	subjects: null,
	aboutCandidates: null,
	members: null,
	users: null,
	directory: null,
};

type Actions =
	| SubjectFetchedAction
	| SubjectsFetchedAction
	| SubjectCreatedAction
	| SubjectUpdatedAction
	| SubjectArchivedAction
	| SubjectRestoredAction
	| AboutCandidatesFetchedAction
	| SubjectMembersFetchedAction
	| SubjectUsersFetchedAction
	| SubjectDirectoryFetchedAction
	| SubjectLeftAction
	| UserRevokedAction;

const whenSubjectFetched = (store: SubjectsStore, action: SubjectFetchedAction): SubjectsStore => ({
	...store,
	subject: new SubjectView(action.payload),
});

// A list entry never carries the write right: only the detail read does.
const whenSubjectsFetched = (store: SubjectsStore, action: SubjectsFetchedAction): SubjectsStore => {
	const { typeId, search, page, rows, totalRecords, results, fieldFilters, fieldSort, includeArchived } =
		action.payload;

	return {
		...store,
		subjects: new SubjectsListView(
			typeId,
			search,
			page,
			rows,
			totalRecords,
			results.map((result) => new SubjectView(result)),
			fieldFilters,
			fieldSort,
			includeArchived,
		),
	};
};

// The creator has written the dossier it just created, whatever the response omits.
const whenSubjectCreated = (store: SubjectsStore, action: SubjectCreatedAction): SubjectsStore => {
	const subject = new SubjectView({ ...action.payload, canWrite: true });
	const isInDisplayedType = store.subjects !== null && store.subjects.typeId === subject.typeId;

	return {
		...store,
		subject,
		subjects:
			isInDisplayedType && store.subjects !== null
				? store.subjects.addSubject(subject)
				: store.subjects,
		directory: null,
	};
};

const whenSubjectUpdated = (store: SubjectsStore, action: SubjectUpdatedAction): SubjectsStore => {
	const subject = new SubjectView(action.payload);

	return {
		...store,
		subject,
		subjects: store.subjects === null ? null : store.subjects.replaceSubject(subject),
		directory: null,
	};
};

// The response carries no subject; the gesture's own screen reads `subject` again, and the other
// screens read their list and directory again on their next mount.
const whenArchivalChanged = (store: SubjectsStore): SubjectsStore => ({
	...store,
	subjects: null,
	directory: null,
});

const whenAboutCandidatesFetched = (
	store: SubjectsStore,
	action: AboutCandidatesFetchedAction,
): SubjectsStore => ({
	...store,
	aboutCandidates: new AboutCandidatesListView(
		action.payload.search,
		action.payload.candidates.map((candidate) => new AboutCandidateView(candidate)),
	),
});

const whenSubjectMembersFetched = (
	store: SubjectsStore,
	action: SubjectMembersFetchedAction,
): SubjectsStore => ({
	...store,
	members: new SubjectMembersListView(
		action.typeKey,
		action.payload.subjectId,
		action.payload.members.map((member) => new SubjectMemberView(member)),
	),
});

const whenSubjectUsersFetched = (store: SubjectsStore, action: SubjectUsersFetchedAction): SubjectsStore => ({
	...store,
	users: new SubjectUsersListView(
		action.typeKey,
		action.payload.subjectId,
		action.payload.users.map((user) => new SubjectUserView(user)),
	),
});

const whenSubjectDirectoryFetched = (
	store: SubjectsStore,
	action: SubjectDirectoryFetchedAction,
): SubjectsStore => ({
	...store,
	directory: new SubjectDirectoryListView(
		action.facilityId,
		action.payload.results.map((entry) => new SubjectDirectoryEntryView(entry)),
	),
});

const whenSubjectLeft = (store: SubjectsStore, action: SubjectLeftAction): SubjectsStore => {
	const { typeKey, subjectId } = action.payload;
	const address = new SubjectAddress(typeKey, subjectId);

	return {
		...store,
		subject:
			typeKey !== BENEFICIARY_SUBJECT_TYPE_KEY && store.subject?.id === subjectId
				? null
				: store.subject,
		members: store.members?.isFor(address) ? null : store.members,
		users: store.users?.isFor(address) ? null : store.users,
	};
};

const whenUserRevoked = (): SubjectsStore => initialState;

export default registerStore<'subjectsStore', Actions>(
	'subjectsStore',
	createSliceReducer({
		SUBJECT_FETCHED: whenSubjectFetched,
		SUBJECTS_FETCHED: whenSubjectsFetched,
		SUBJECT_CREATED: whenSubjectCreated,
		SUBJECT_UPDATED: whenSubjectUpdated,
		SUBJECT_ARCHIVED: whenArchivalChanged,
		SUBJECT_RESTORED: whenArchivalChanged,
		ABOUT_CANDIDATES_FETCHED: whenAboutCandidatesFetched,
		SUBJECT_MEMBERS_FETCHED: whenSubjectMembersFetched,
		SUBJECT_USERS_FETCHED: whenSubjectUsersFetched,
		SUBJECT_DIRECTORY_FETCHED: whenSubjectDirectoryFetched,
		SUBJECT_LEFT: whenSubjectLeft,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
